import type { ArticleData } from '@/types';
import type { ProcessedFile } from '@/utils/fileUploadUtils';

// Define the structure for the conversation history
export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

// Define the request payload for the contextual tutor API
interface TutorRequestPayload {
  question: string;
  articleContext: ArticleData;
  conversationHistory: ConversationTurn[];
  userLanguage: string;
  attachedFiles?: ProcessedFile[];
}

// Define the successful response structure from the API
interface TutorSuccessResponse {
  success: true;
  answer: string;
}

// Define the error response structure from the API
interface TutorErrorResponse {
  success: false;
  error: string;
}

type TutorApiResponse = TutorSuccessResponse | TutorErrorResponse;

/**
 * Communicates with the contextual AI tutor backend.
 * 
 * @param question - The user's question.
 * @param articleContext - The article content to provide context.
 * @param conversationHistory - The history of the current conversation.
 * @param userLanguage - The user's preferred language code.
 * @param attachedFiles - Optional files to attach to the query.
 * @returns The AI's answer as a string.
 * @throws Will throw an error if the API call fails or returns an error.
 */
export const askContextualTutor = async (
  question: string,
  articleContext: ArticleData,
  conversationHistory: ConversationTurn[],
  userLanguage: string,
  attachedFiles?: ProcessedFile[]
): Promise<string> => {
  // Log file details before sending
  if (attachedFiles && attachedFiles.length > 0) {
    console.log('[Tutor Service] Preparing files for upload:');
    attachedFiles.forEach((file, idx) => {
      const contentSize = file.content?.length || 0;
      console.log(`  File ${idx + 1}: ${file.name}`);
      console.log(`    - Type: ${file.type}`);
      console.log(`    - Content size: ${contentSize} bytes (${(contentSize / 1024).toFixed(2)} KB)`);
      console.log(`    - Content start: ${file.content?.substring(0, 50) || 'empty'}...`);
    });
  }

  const payload: TutorRequestPayload = {
    question,
    articleContext,
    conversationHistory,
    userLanguage,
    attachedFiles,
  };

  try {
    // Use relative path so it works on any domain
    const endpoint = '/api/ai/contextual-tutor';
    
    const payloadSize = JSON.stringify(payload).length;
    console.log('[Tutor Service] Calling endpoint:', endpoint);
    console.log('  - Files:', attachedFiles?.length || 0);
    console.log('  - Payload size:', (payloadSize / 1024).toFixed(2), 'KB');
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData: TutorApiResponse = await response.json();

    if (response.ok && responseData.success) {
      return (responseData as TutorSuccessResponse).answer;
    } else {
      const errorMessage = (responseData as TutorErrorResponse).error || 'An unknown error occurred in the AI service.';
      throw new Error(errorMessage);
    }
  } catch (error) {
    // Log the error for debugging purposes
    console.error('Contextual Tutor API call failed:', error);

    // Re-throw a more user-friendly error message
    if (error instanceof Error) {
      throw new Error(`Failed to get a response from the tutor. Reason: ${error.message}`);
    }
    throw new Error('An unknown error occurred while trying to contact the tutor.');
  }
};
